import { useMemo, useState } from 'react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Button } from '@/components/ui/Button';
import { recordTitle } from '@/components/apps/app-records-model';
import type { AppCollection, AppRecord } from '@/lib/app-records';
import { outreachAction } from '@/lib/outreach';

interface CampaignAddDrawerProps {
  sequenceId: string;
  collection: AppCollection;
  records: AppRecord[];
  inCampaign: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

/**
 * « Ajouter depuis la liste » (04/11/2026): objects of the app's list enter
 * the campaign; they stay in the list and in their other campaigns.
 */
export const CampaignAddDrawer = ({ sequenceId, collection, records, inCampaign, onClose, onAdded }: CampaignAddDrawerProps) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const outside = useMemo(() => records.filter((record) => !record.archivedAt && !inCampaign.has(record.id)), [records, inCampaign]);
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase();
    return outside.filter((record) => (!status || record.status === status)
      && (!words || record.computed.some((cell) => String(cell.value ?? '').toLowerCase().includes(words))));
  }, [outside, query, status]);
  const statuses = collection.statuses.filter((item) => outside.some((record) => record.status === item.key));
  const allOn = shown.length > 0 && shown.every((record) => picked.has(record.id));

  const add = async () => {
    setBusy(true);
    try {
      const answer = await outreachAction<{ added: number; inOtherCampaigns: number; otherCampaigns: string[] }>({ action: 'add_to_campaign', sequenceId, recordIds: [...picked] });
      setResult(`${answer.added} ajoutée(s)${answer.inOtherCampaigns ? ` · ${answer.inOtherCampaigns} aussi dans ${answer.otherCampaigns.map((name) => `« ${name} »`).join(', ')}` : ''}`);
      setPicked(new Set());
      onAdded();
    } catch (cause) {
      setResult(cause instanceof Error ? cause.message : 'Rien n’a été ajouté.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SideDrawer label="Ajouter depuis la liste" title="Ajouter depuis la liste" onClose={onClose}
      actions={picked.size > 0 && <Button size="sm" loading={busy} disabled={busy} onClick={() => { void add(); }}>Ajouter {picked.size}</Button>}>
      <div className="space-y-3 p-4">
        {result && <p role="status" className="text-sm font-semibold text-domino-ink">{result}</p>}
        <div className="flex flex-wrap gap-2">
          <input type="search" aria-label="Rechercher" placeholder="Rechercher" value={query} onChange={(event) => setQuery(event.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none" />
          {statuses.length > 1 && (
            <select aria-label="Statut" value={status} onChange={(event) => setStatus(event.target.value)}
              className="rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none">
              <option value="">Tous les statuts</option>
              {statuses.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          )}
        </div>
        {shown.length === 0
          ? <p className="text-sm text-domino-inkSoft">{outside.length === 0 ? 'Toute la liste est déjà dans la campagne' : 'Aucun résultat'}</p>
          : (
            <ul className="divide-y divide-domino-cardBorder/60 rounded-xl border border-domino-cardBorder bg-domino-white">
              <li className="px-3 py-2">
                <label className="flex items-center gap-2 text-xs text-domino-inkSoft">
                  <input type="checkbox" checked={allOn}
                    onChange={(event) => setPicked((current) => {
                      const next = new Set(current);
                      for (const record of shown) if (event.target.checked) next.add(record.id); else next.delete(record.id);
                      return next;
                    })} />
                  {shown.length} objet{shown.length > 1 ? 's' : ''}
                </label>
              </li>
              {shown.map((record) => (
                <li key={record.id} className="px-3 py-2">
                  <label className="flex items-center gap-2 text-sm text-domino-ink">
                    <input type="checkbox" checked={picked.has(record.id)}
                      onChange={(event) => setPicked((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(record.id); else next.delete(record.id);
                        return next;
                      })} />
                    <span className="truncate">{recordTitle(record, collection)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
      </div>
    </SideDrawer>
  );
};
